import { HeartHandshake, LifeBuoy, Users } from 'lucide-react';
import { Link } from 'react-router-dom';
import { FigureMotif } from '@/components/brand/FigureMotif';
import { Container } from '@/components/layout/Container';
import { Reveal } from '@/components/motion/Reveal';
import { Section, SectionHeading } from '@/components/layout/Section';
import { QueryBoundary } from '@/components/feedback/QueryBoundary';
import { EmptyState } from '@/components/feedback/states';
import { Button } from '@/components/ui/button';
import { Skeleton } from '@/components/ui/skeleton';
import { Seo } from '@/lib/seo/Seo';
import { ArticleCard } from '@/features/blog/ArticleCard';
import { useArticles } from '@/features/blog/api';
import { ImpactStats } from '@/features/donations/ImpactStats';
import { EventCard } from '@/features/events/EventCard';
import { useEvents } from '@/features/events/api';
import { Hero } from '@/features/home/Hero';

const PROGRAMS = [
  {
    icon: LifeBuoy,
    title: 'Confidential support',
    body: 'A safe, judgement-free path to talk to someone who understands.',
  },
  {
    icon: Users,
    title: 'Community & mentorship',
    body: 'Connect with other men and mentors walking the same road.',
  },
  {
    icon: HeartHandshake,
    title: 'Resources that help',
    body: 'Practical guides, tools and referrals for you and your family.',
  },
];

export function HomePage() {
  const articles = useArticles({ limit: 3 });
  const events = useEvents({ limit: 3 });

  return (
    <>
      <Seo
        title="Care For Him"
        description="A men's mental health initiative offering confidential support, resources, events and community."
        jsonLd={{
          '@context': 'https://schema.org',
          '@type': 'Organization',
          name: 'Care For Him',
          description: "Men's mental health initiative",
        }}
      />

      {/* Hero */}
      <Hero />

      {/* Impact band */}
      <Section tinted className="py-12 md:py-14">
        <Reveal>
          <ImpactStats />
        </Reveal>
      </Section>

      {/* Programs */}
      <Section>
        <SectionHeading
          eyebrow="How we help"
          title="Support built around men"
          description="Every part of Care For Him is designed to make reaching out easier."
          align="center"
        />
        <div className="grid gap-6 md:grid-cols-3">
          {PROGRAMS.map((p, i) => (
            <Reveal key={p.title} delay={i * 0.1}>
              <div className="group h-full rounded-[var(--radius-card)] border border-line bg-surface p-7 transition-all duration-300 hover:-translate-y-1 hover:border-purple-500/30 hover:shadow-[var(--shadow-elevated)]">
                <span className="inline-flex h-12 w-12 items-center justify-center rounded-xl bg-purple-50 text-purple-600 transition-transform duration-300 group-hover:scale-110">
                  <p.icon className="h-6 w-6" />
                </span>
                <h3 className="mt-4 text-lg font-bold text-ink">{p.title}</h3>
                <p className="mt-2 text-sm text-muted">{p.body}</p>
              </div>
            </Reveal>
          ))}
        </div>
      </Section>

      {/* Get Help pathway */}
      <section className="bg-[color:var(--color-purple-deep)] py-16 text-white md:py-20">
        <Container className="flex flex-col items-center gap-6 text-center">
          <FigureMotif className="h-16 w-16 text-white/80" />
          <h2 className="max-w-2xl text-3xl font-bold md:text-4xl">
            Talking to someone is the first step. We'll take it with you.
          </h2>
          <p className="max-w-xl text-white/80">
            Reach out confidentially — no pressure, no judgement. We'll respond privately and point
            you to the right support.
          </p>
          <Button asChild size="lg" variant="secondary">
            <Link to="/get-help">Reach out now</Link>
          </Button>
        </Container>
      </section>

      {/* Latest news */}
      <Section>
        <SectionHeading
          eyebrow="News & stories"
          title="Latest from Care For Him"
          action={
            <Button asChild variant="link">
              <Link to="/news">All news →</Link>
            </Button>
          }
        />
        <QueryBoundary
          query={articles}
          skeleton={
            <div className="grid gap-6 md:grid-cols-3">
              {Array.from({ length: 3 }).map((_, i) => (
                <Skeleton key={i} className="h-72" />
              ))}
            </div>
          }
          isEmpty={(d) => d.items.length === 0}
          empty={<EmptyState title="No stories yet" description="Check back soon for updates." />}
        >
          {(data) => (
            <div className="grid gap-6 md:grid-cols-3">
              {data.items.map((a) => (
                <ArticleCard key={a.id} article={a} />
              ))}
            </div>
          )}
        </QueryBoundary>
      </Section>

      {/* Upcoming events */}
      <Section tinted>
        <SectionHeading
          eyebrow="What's on"
          title="Upcoming events"
          action={
            <Button asChild variant="link">
              <Link to="/events">All events →</Link>
            </Button>
          }
        />
        <QueryBoundary
          query={events}
          skeleton={
            <div className="grid gap-6 md:grid-cols-3">
              {Array.from({ length: 3 }).map((_, i) => (
                <Skeleton key={i} className="h-44" />
              ))}
            </div>
          }
          isEmpty={(d) => d.items.length === 0}
          empty={<EmptyState title="No upcoming events" description="New events are added regularly." />}
        >
          {(data) => (
            <div className="grid gap-6 md:grid-cols-3">
              {data.items.map((e) => (
                <EventCard key={e.id} event={e} />
              ))}
            </div>
          )}
        </QueryBoundary>
      </Section>

      {/* Donate banner */}
      <section className="py-16 md:py-20">
        <Container>
          <Reveal>
            <div className="relative overflow-hidden rounded-[var(--radius-card)] bg-gradient-to-r from-pink-500 to-teal-500 p-10 text-center text-white shadow-[var(--shadow-elevated)] md:p-14">
              <div aria-hidden className="absolute inset-0 bg-grid opacity-20" />
              <FigureMotif
                aria-hidden
                className="pointer-events-none absolute -right-6 -top-6 h-40 w-40 text-white/15"
              />
              <div className="relative">
                <h2 className="text-3xl font-bold md:text-4xl">
                  Your gift keeps a man from facing it alone
                </h2>
                <p className="mx-auto mt-3 max-w-xl text-white/90">
                  Every donation funds confidential support, resources and community programs.
                </p>
                <Button
                  asChild
                  size="lg"
                  variant="ghost"
                  className="mt-6 bg-white text-[color:var(--color-purple-deep)] hover:bg-white/90"
                >
                  <Link to="/donate">Donate today</Link>
                </Button>
              </div>
            </div>
          </Reveal>
        </Container>
      </section>
    </>
  );
}
